import { SignUpProductEnum } from "components/Signup/flow/SignupFlow/types";
import { SpeciesEnum } from "../../../../../__generated__/globalTypes";
import { StepName } from "../../steps/types";
import { Insurely } from "./types/insurely";
import { LifeUtilityAddOn } from "components-shared/insuranceData";
import { UpsertDraft_upsertDraft_policyPreviews } from "../../../../../__generated__/UpsertDraft";
import { CheckMultiBind_checkMultiBind } from "../../../../../__generated__/CheckMultiBind";
import { BillingInterval } from "utils/insurance";

export type Sex = "male" | "female";
export type Castration = "castrated" | "not-castrated";
export type Access = "outdoor" | "indoor";

export enum HealthHistoryEnum {
  NO_ILLNESS = "NO_ILLNESS",
  ILLNESS_LAST_24_MONTHS = "ILLNESS_LAST_24_MONTHS",
  TREATED_LAST_24_MONTHS = "TREATED_LAST_24_MONTHS",
  BREED_SPECIFIC_ILLNESS = "BREED_SPECIFIC_ILLNESS",
  CHRONIC_DISEASE = "CHRONIC_DISEASE",
  IMPORTED = "IMPORTED",
}

export type LiabilityNumberOfClaimsHistory =
  | "no_claims"
  | "one_claim"
  | "two_claims"
  | "three_or_more_claims";

export type LiabilityCancelledPreviously = "YES" | "NO";

export type MinimalBreedInfo = {
  name: string;
  id: string;
  lifeAgeGroup: number;
};

export enum CampaignCode {
  Lunar = "lunar",
  Eurobonus = "eurobonus",
}

export type ResumeDraftState = {
  isLiability: boolean;
  previewId: string | null;
  name: string | null;
  species: SpeciesEnum | null;
  isMultipet: boolean;
};

export type Navigation = {
  isTransitioning: boolean;
  activeStep: StepName;
  isValid: boolean;
};

export type Metadata = {
  readonly deviceId: string;
  readonly sessionId: string;
  fromApp?: boolean;
  returnUrl: string | null;
  purchaseCompleted: boolean;
  campaignCode?: CampaignCode | null;
  draftId: string | null;
  error: Error | null;
  loading: boolean;
  isResumed: boolean;
  applyPreventiveCareToAll?: boolean;
};

export type Insurance = {
  id: string;
  billingInterval: BillingInterval;
  billingDay: number;
  copay: number;
  deductible: number;
  limit: number;
  startDate: string;
  product: SignUpProductEnum | null;
  drafts: UpsertDraft_upsertDraft_policyPreviews["plans"] | undefined;
  discountCode: string | null;
  preventiveCareAddOnLimit?: number;
  liabilityBestAddOn?: boolean;
  liabilitySosAddOn?: boolean;
  liabilityRentedAddOn?: boolean;
  liabilityNumberOfClaimsHistory?: LiabilityNumberOfClaimsHistory | null;
  liabilityCancelledByPreviousInsuranceCompany?: LiabilityCancelledPreviously | null;
  insurely?: Insurely;
  breedersAddOn?: boolean | null;
  lifeLimit?: number | null;
  showLifeInsurance?: boolean;
  lifeUtilityAddOn?: LifeUtilityAddOn;
  preexistingConditions?: boolean | null;
  selectedLimitAddon?: number;
  previousInsurance?: boolean | null;
  policy: CheckMultiBind_checkMultiBind["policy"] | null;
};

export type Animal = {
  id: string;
  name: string | null;
  sex: Sex | null;
  sexSituation: Castration | null;
  birthday: string | null;
  species: SpeciesEnum | null;
  access: Access | null;
  breed: { id: string; name: string; lifeAgeGroup: number } | null;
  mixedBreedFHalf: { id: string; name: string; lifeAgeGroup: number } | null;
  mixedBreedSHalf: { id: string; name: string; lifeAgeGroup: number } | null;
  insurances: Insurance[];
  healthHistory: HealthHistoryEnum[];
  isDraft: boolean;
};

export type Address = {
  city: string | null;
  line1: string | null;
  line2?: string | null;
  zip: string | null;
};

export type Customer = {
  email: string | null;
  phone: string | null;
  firstName: string | null;
  lastName: string | null;
  birthdate: string | null;
  region: string | null;
  address?: Address;
  privacyAccepted: boolean;
  marketingPermissionAccepted: boolean;
  termsAccepted: boolean;
  ssn?: string | null;
  eurobonusMemberId?: string | null;
  eurobonusMemberIdValid?: boolean;
  lunarMemberId?: string | null;
  lunarMemberIdValid?: boolean;
  clientId: string | null;
};

export type State = {
  metadata: Metadata;
  animals: Animal[];
  customer: Customer;
  navigation: Navigation;
};
